import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useParams, useLocation } from 'react-router-dom';
import './App.css';
import FeatureTable from './components/FeatureTable';
import FeaturePanel from './components/FeaturePanel';
import ImprovePromptView from './components/ImprovePromptView';
import Auth from './components/Auth';
import Home from './components/Home';
import FeatureGroupAdmin from './components/FeatureGroupAdmin';
import Tags from './components/Tags';
import Releases from './components/Releases';
import Competitors from './components/Competitors';
import SystemProfile from './components/SystemProfile';
import SystemAdmin from './components/SystemAdmin';
import UserMenu from './components/UserMenu';
import RecentFeatures from './components/RecentFeatures';
import Footer from './components/Footer';
import About from './components/About';
import Privacy from './components/Privacy';
import Terms from './components/Terms';
import { useAuth } from './contexts/AuthContext';
import BuyFeaturePage from './components/BuyFeaturePage';
import { getAllFeatures, createFeature, updateFeature, deleteFeature, moveFeature, getUserFeatureGroups, getUserRoleInFeatureGroup, setFeatureTags, getCurrentUserProfile, updateUserProfile, updateLastVisit, getFeatureById, getSystemSetting } from './services/featureService';
import { setFeatureReleases, getAllFeatureReleasesForGroup, getCurrentRelease } from './services/releaseService';
import { supabase } from './lib/supabase';
import { logger, handleError } from './utils/logger';

function AppContent() {
  const { user, loading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { featureId } = useParams();
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState('view'); // 'view', 'edit', or 'add'
  const [viewingFeature, setViewingFeature] = useState(null);
  const [editingFeature, setEditingFeature] = useState(null);
  const [parentFeature, setParentFeature] = useState(null);
  const [filter, setFilter] = useState('');
  const [activeTab, setActiveTab] = useState('Home');
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [featureGroups, setFeatureGroups] = useState([]);
  const [selectedFeatureGroup, setSelectedFeatureGroup] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [hierarchyMode, setHierarchyMode] = useState('edit'); // 'edit', 'userVote', 'adminVote'
  const [featureReleasesCache, setFeatureReleasesCache] = useState(new Map()); // featureId -> releases array
  const [currentRelease, setCurrentRelease] = useState(null); // Current active release for footer display
  const [footerFeatureGroupId, setFooterFeatureGroupId] = useState(null); // Feature group to use for footer release
  const [showRecentFeatures, setShowRecentFeatures] = useState(false); // Show recent features modal
  const [showImprovePrompt, setShowImprovePrompt] = useState(false); // Show improve prompt view
  const [improvePromptFeature, setImprovePromptFeature] = useState(null); // Feature for improve prompt
  const [improvePromptLoading, setImprovePromptLoading] = useState(false); // Loading state for improve prompt

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMobileMenu && !event.target.closest('.mobile-menu-wrapper')) {
        setShowMobileMenu(false);
      }
    };

    if (showMobileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showMobileMenu]);

  useEffect(() => {
    if (user) {
      loadFeatureGroups();
      loadUserProfile();
      loadFooterFeatureGroup();
    } else {
      setLoading(false);
      setUserProfile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);


  const loadFooterFeatureGroup = async () => {
    try {
      const setting = await getSystemSetting('footer_feature_group_id');
      setFooterFeatureGroupId(setting);
    } catch (err) {
      logger.error('Error loading footer feature group setting:', err);
    }
  };

  const loadFooterRelease = async () => {
    if (!footerFeatureGroupId) {
      setCurrentRelease(null);
      return;
    }
    try {
      const release = await getCurrentRelease(footerFeatureGroupId);
      logger.debug('Footer release loaded:', { 
        release, 
        hasSubversion: !!release?.current_subversion,
        featureGroupId: footerFeatureGroupId 
      });
      setCurrentRelease(release);
    } catch (err) {
      logger.error('Error loading footer release:', err);
      setCurrentRelease(null);
    }
  };

  // Periodically refresh footer release (every 5 minutes) to catch date changes
  useEffect(() => {
    if (!footerFeatureGroupId) return;

    // Load immediately
    loadFooterRelease();

    // Then refresh every 5 minutes
    const interval = setInterval(() => {
      loadFooterRelease();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [footerFeatureGroupId]);

  useEffect(() => {
    if (selectedFeatureGroup) {
      loadFeatures();
      loadUserRole();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeatureGroup]);

      // Switch to Home tab if no feature map is selected and user is on a disabled tab
      useEffect(() => {
        if (!selectedFeatureGroup && activeTab !== 'Home') {
          const disabledTabs = ['Hierarchy', 'Releases', 'Tags', 'Competitors', 'Analysis', 'Admin'];
          if (disabledTabs.includes(activeTab)) {
            setActiveTab('Home');
          }
        }
      }, [selectedFeatureGroup, activeTab]);

  // Handle direct feature links from URL
  useEffect(() => {
    const handleFeatureRoute = async () => {
      if (!featureId || !user) return;

      // Helper to find feature in tree
      const findFeature = (featuresList, targetId) => {
        for (const f of featuresList) {
          if (f.id === targetId) return f;
          if (f.subFeatures && f.subFeatures.length > 0) {
            const found = findFeature(f.subFeatures, targetId);
            if (found) return found;
          }
        }
        return null;
      };

      // Helper to expand parent nodes
      const expandParents = (featuresList, targetId, expanded) => {
        for (const f of featuresList) {
          if (f.id === targetId) {
            return true;
          }
          if (f.subFeatures && f.subFeatures.length > 0) {
            if (expandParents(f.subFeatures, targetId, expanded)) {
              expanded.add(f.id);
              return true;
            }
          }
        }
        return false;
      };

      // If no feature group is selected, we need to fetch the feature first
      // to determine which feature group it belongs to
      if (!selectedFeatureGroup) {
        try {
          const feature = await getFeatureById(featureId);
          if (feature && feature.feature_group_id) {
            // Check if user has access to this feature group
            const userGroups = featureGroups.length > 0 ? featureGroups : await getUserFeatureGroups();
            const hasAccess = userGroups.some(g => g.id === feature.feature_group_id);
            
            if (hasAccess) {
              // Select the feature group - this will trigger loadFeatures via the other useEffect
              setSelectedFeatureGroup(feature.feature_group_id);
              // Update last visit
              await updateLastVisit(feature.feature_group_id, user.id);
            } else {
              logger.error('User does not have access to this feature group');
              navigate('/');
            }
          } else {
            logger.error('Feature not found or has no feature group');
            navigate('/');
          }
        } catch (err) {
          logger.error('Error fetching feature:', err);
          navigate('/');
        }
        return;
      }

      // Feature group is selected, now find/load the feature
      let feature = findFeature(features, featureId);
      
      // If not found in loaded features, try to fetch and reload
      if (!feature && features.length > 0) {
        try {
          const fetchedFeature = await getFeatureById(featureId);
          if (fetchedFeature) {
            // Reload features to include it in the tree
            const reloadedFeatures = await loadFeatures();
            feature = findFeature(reloadedFeatures, featureId);
          }
        } catch (err) {
          logger.error('Error fetching feature:', err);
          navigate('/');
          return;
        }
      }

      // If features haven't loaded yet, wait for them
      if (features.length === 0) {
        return; // Will re-run when features are loaded
      }

      if (feature) {
        // Only switch to Hierarchy tab if we're not already on a content tab
        // This preserves the current tab (e.g., Releases) when opening from within the app
        const contentTabs = ['Hierarchy', 'Releases', 'Tags', 'Competitors', 'Admin', 'Analysis'];
        if (!contentTabs.includes(activeTab)) {
          setActiveTab('Hierarchy');
        }
        // Expand parent nodes to show the feature (only if on Hierarchy tab)
        if (activeTab === 'Hierarchy') {
          const newExpanded = new Set(expandedNodes);
          expandParents(features, featureId, newExpanded);
          setExpandedNodes(newExpanded);
        }
        // Open the feature in view mode
        setViewingFeature(feature);
        setEditingFeature(null);
        setParentFeature(null);
        setPanelMode('view');
        setPanelOpen(true);
      } else {
        // Feature not found, navigate away
        navigate('/');
      }
    };

    if (location.pathname.startsWith('/feature/')) {
      handleFeatureRoute();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureId, user, selectedFeatureGroup, features, featureGroups, location.pathname, activeTab]);

  const loadFeatureGroups = async () => {
    try {
      const groups = await getUserFeatureGroups();
      setFeatureGroups(groups);
      // Don't auto-select - user must select from Home page
    } catch (err) {
      logger.error('Error loading feature groups:', err);
      setError(handleError(err, 'Failed to load feature groups. Please try again.'));
    }
  };

  const loadUserProfile = async () => {
    try {
      setProfileLoading(true);
      const profile = await getCurrentUserProfile();
      setUserProfile(profile);
    } catch (err) {
      logger.error('Error loading user profile:', err);
    } finally {
      setProfileLoading(false);
    }
  };

  const loadUserRole = async () => {
    if (!selectedFeatureGroup) return;
    try {
      const role = await getUserRoleInFeatureGroup(selectedFeatureGroup);
      setUserRole(role);
    } catch (err) {
      logger.error('Error loading user role:', err);
    }
  };

  const getParentName = (parentId) => {
    if (!parentId) return 'None (Root)';
    const findParent = (featuresList, targetId) => {
      for (const f of featuresList) {
        if (f.id === targetId) return f.name;
        if (f.subFeatures && f.subFeatures.length > 0) {
          const found = findParent(f.subFeatures, targetId);
          if (found) return found;
        }
      }
      return null;
    };
    return findParent(features, parentId) || 'Unknown';
  };

  const logFeatureChanges = (beforeFeatures, afterFeatures) => {
    const flattenFeatures = (featuresList) => {
      let result = [];
      featuresList.forEach(f => {
        result.push(f);
        if (f.subFeatures && f.subFeatures.length > 0) {
          result = result.concat(flattenFeatures(f.subFeatures));
        }
      });
      return result;
    };

    const beforeFlat = flattenFeatures(beforeFeatures);
    const afterFlat = flattenFeatures(afterFeatures);
    
    const beforeMap = new Map(beforeFlat.map(f => [f.id, f]));
    const afterMap = new Map(afterFlat.map(f => [f.id, f]));

    logger.debug('=== FEATURE SAVE LOG ===');
    
    // Find new or updated features
    afterFlat.forEach(afterFeature => {
      const beforeFeature = beforeMap.get(afterFeature.id);
      const parentName = getParentName(afterFeature.parent_id);
      
      if (!beforeFeature) {
        // New feature
        logger.debug('🆕 NEW FEATURE:', {
          name: afterFeature.name,
          id: afterFeature.id,
          parentName: parentName,
          parentId: afterFeature.parent_id || 'null'
        });
      } else if (
        beforeFeature.name !== afterFeature.name ||
        beforeFeature.parent_id !== afterFeature.parent_id
      ) {
        // Updated feature
        const beforeParentName = getParentName(beforeFeature.parent_id);
        logger.debug('✏️ UPDATED FEATURE:', {
          name: afterFeature.name,
          id: afterFeature.id,
          parentName: parentName,
          parentId: afterFeature.parent_id || 'null',
          previousParentName: beforeParentName,
          previousParentId: beforeFeature.parent_id || 'null',
          nameChanged: beforeFeature.name !== afterFeature.name
        });
      }
    });

    // Find deleted features
    beforeFlat.forEach(beforeFeature => {
      if (!afterMap.has(beforeFeature.id)) {
        logger.debug('🗑️ DELETED FEATURE:', {
          name: beforeFeature.name,
          id: beforeFeature.id,
          parentName: getParentName(beforeFeature.parent_id),
          parentId: beforeFeature.parent_id || 'null'
        });
      }
    });

    logger.debug('=== END FEATURE SAVE LOG ===');
  };

  // Extract getAllFeatureIds to module level to avoid recreation
  const getAllFeatureIds = useCallback((featuresList) => {
    const ids = new Set();
    const collectIds = (items) => {
      items.forEach(item => {
        ids.add(item.id);
        if (item.subFeatures && item.subFeatures.length > 0) {
          collectIds(item.subFeatures);
        }
      });
    };
    collectIds(featuresList);
    return ids;
  }, []);

  const loadFeatures = useCallback(async (preserveExpanded = true, featureGroupId = null) => {
    try {
      setLoading(true);
      setError(null);
      
      // Use provided featureGroupId or fall back to selectedFeatureGroup state
      const groupIdToLoad = featureGroupId || selectedFeatureGroup;
      if (!groupIdToLoad) {
        setLoading(false);
        return [];
      }
      
      // Save current expanded nodes before reload
      const savedExpanded = preserveExpanded ? new Set(expandedNodes) : new Set();
      
      // Load features and releases in parallel for better performance
      // Note: Footer release is loaded separately based on footerFeatureGroupId setting
      const [data, releasesMap] = await Promise.all([
        getAllFeatures(groupIdToLoad, userRole),
        getAllFeatureReleasesForGroup(groupIdToLoad)
      ]);
      
      setFeatures(data);
      setFeatureReleasesCache(releasesMap);
      
      // Only update currentRelease if this is the footer feature group
      if (groupIdToLoad === footerFeatureGroupId) {
        const activeRelease = await getCurrentRelease(groupIdToLoad);
        setCurrentRelease(activeRelease);
      }
      
      // Restore expanded nodes after reload (cleaning up deleted ones)
      if (preserveExpanded && savedExpanded.size > 0) {
        const allIds = getAllFeatureIds(data);
        const cleaned = new Set();
        savedExpanded.forEach(id => {
          if (allIds.has(id)) {
            cleaned.add(id);
          }
        });
        setExpandedNodes(cleaned);
      }
      
      return data;
    } catch (err) {
      setError(handleError(err, 'Failed to load features. Please try again.'));
      logger.error(err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [selectedFeatureGroup, expandedNodes, footerFeatureGroupId, getAllFeatureIds, userRole]);

  // Set up realtime subscriptions for feature changes (after loadFeatures is defined)
  useEffect(() => {
    if (!selectedFeatureGroup) return;
    
    // Subscribe to feature updates
    const channel = supabase
      .channel(`features:${selectedFeatureGroup}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'features',
          filter: `feature_group_id=eq.${selectedFeatureGroup}`
        },
        (payload) => {
          logger.debug('Realtime feature change:', payload);
          
          // Handle UPDATE events
          if (payload.eventType === 'UPDATE') {
            const updatedFeature = payload.new;
            
            // Update the feature in the features list
            const updateFeatureInTree = (featuresList) => {
              return featuresList.map(f => {
                if (f.id === updatedFeature.id) {
                  return { ...f, ...updatedFeature };
                }
                if (f.subFeatures && f.subFeatures.length > 0) {
                  return { ...f, subFeatures: updateFeatureInTree(f.subFeatures) };
                }
                return f;
              });
            };
            
            setFeatures(prevFeatures => updateFeatureInTree(prevFeatures));
            
            // If this is the currently viewing/editing feature, update it
            if (viewingFeature && viewingFeature.id === updatedFeature.id) {
              setViewingFeature(prev => ({ ...prev, ...updatedFeature }));
            }
            if (editingFeature && editingFeature.id === updatedFeature.id) {
              setEditingFeature(prev => ({ ...prev, ...updatedFeature }));
            }
          }
          
          // Handle INSERT events (new features)
          if (payload.eventType === 'INSERT') {
            // Reload features to get the new feature in the correct position
            loadFeatures(false, selectedFeatureGroup);
          }
          
          // Handle DELETE events
          if (payload.eventType === 'DELETE') {
            const deletedId = payload.old.id;
            
            // Remove from features list
            const removeFeatureFromTree = (featuresList) => {
              return featuresList
                .filter(f => f.id !== deletedId)
                .map(f => {
                  if (f.subFeatures && f.subFeatures.length > 0) {
                    return { ...f, subFeatures: removeFeatureFromTree(f.subFeatures) };
                  }
                  return f;
                });
            };
            
            setFeatures(prevFeatures => removeFeatureFromTree(prevFeatures));
            
            // Close panel if viewing/editing deleted feature
            if ((viewingFeature && viewingFeature.id === deletedId) || 
                (editingFeature && editingFeature.id === deletedId)) {
              setPanelOpen(false);
              setViewingFeature(null);
              setEditingFeature(null);
            }
          }
        }
      )
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedFeatureGroup, viewingFeature, editingFeature, loadFeatures]);

  const handleAddFeature = useCallback((parent = null) => {
    // Clear all feature-related state to ensure a fresh form
    setEditingFeature(null);
    setViewingFeature(null);
    setParentFeature(parent);
    setPanelMode('add');
    setPanelOpen(true);
  }, []);

  const handleViewFeature = useCallback((feature) => {
    setViewingFeature(feature);
    setEditingFeature(null);
    setParentFeature(null);
    setPanelMode('view');
    setPanelOpen(true);
    // Update URL to include feature ID
    navigate(`/feature/${feature.id}`);
  }, [navigate]);

  const handleNavigateToFeature = useCallback((featureId) => {
    // Navigate to feature by ID
    navigate(`/feature/${featureId}`);
  }, [navigate]);

  const handleEditFeature = useCallback((feature) => {
    setEditingFeature(feature);
    setViewingFeature(feature);
    setParentFeature(null);
    setPanelMode('edit');
    setPanelOpen(true);
  }, []);

  const handleStartEdit = useCallback(() => {
    if (viewingFeature) {
      setEditingFeature(viewingFeature);
      setPanelMode('edit');
    }
  }, [viewingFeature]);

  const handleCancelToView = useCallback(() => {
    setPanelMode('view');
    setEditingFeature(null);
  }, []);

  const handleOpenFeatureFromHome = useCallback((featureId) => {
    // Navigating to /feature/:id will trigger the existing route-based
    // logic to load and open the slide-out panel for that feature
    navigate(`/feature/${featureId}`);
  }, [navigate]);

  const handleDeleteFeature = useCallback(async (id) => {
    // Note: Confirmation is now handled by the in-app dialog in FeatureTable
    // This function is called after user confirms in the dialog
    try {
      await deleteFeature(id);
      await loadFeatures();
    } catch (err) {
      setError(handleError(err, 'Failed to delete feature. Please try again.'));
      logger.error(err);
    }
  }, [loadFeatures]);

  const handleSaveFeature = async (featureData, tagAssignments = [], selectedReleaseIds = []) => {
    try {
      const featuresBefore = JSON.parse(JSON.stringify(features)); // Deep copy
      
      if (editingFeature) {
        logger.debug('💾 SAVING (UPDATE):', {
          name: featureData.name,
          id: editingFeature.id,
          parentName: getParentName(featureData.parent_id),
          parentId: featureData.parent_id || 'null'
        });
        
        await updateFeature(editingFeature.id, featureData);
        // Save tags if provided (tagAssignments format: [{tag_id, state, rating, comment}])
        if (tagAssignments !== undefined && tagAssignments.length > 0) {
          await setFeatureTags(editingFeature.id, tagAssignments);
        }
        // Save releases if provided
        if (selectedReleaseIds !== undefined) {
          await setFeatureReleases(editingFeature.id, selectedReleaseIds);
        }
        // Reload features to get updated data (including sort_order changes)
        const updatedFeatures = await loadFeatures();
        
        logFeatureChanges(featuresBefore, updatedFeatures);
        
        // Find and update the viewing feature with fresh data
        const findFeature = (featuresList, targetId) => {
          for (const f of featuresList) {
            if (f.id === targetId) return f;
            if (f.subFeatures && f.subFeatures.length > 0) {
              const found = findFeature(f.subFeatures, targetId);
              if (found) return found;
            }
          }
          return null;
        };
        const updatedFeature = findFeature(updatedFeatures, editingFeature.id);
        if (updatedFeature) {
          setViewingFeature(updatedFeature);
        } else {
          // Fallback: update with provided data
          setViewingFeature({ ...viewingFeature, ...featureData });
        }
        // Switch to view mode after save
        setEditingFeature(null);
        setPanelMode('view');
      } else {
        logger.debug('💾 SAVING (CREATE):', {
          name: featureData.name,
          id: 'NEW',
          parentName: getParentName(featureData.parent_id),
          parentId: featureData.parent_id || 'null'
        });
        
        const newFeature = await createFeature({ ...featureData, feature_group_id: selectedFeatureGroup });
        // Save tags for new feature (tagAssignments format)
        if (tagAssignments && tagAssignments.length > 0) {
          await setFeatureTags(newFeature.id, tagAssignments);
        }
        // Save releases for new feature
        if (selectedReleaseIds && selectedReleaseIds.length > 0) {
          await setFeatureReleases(newFeature.id, selectedReleaseIds);
        }
        // Reload features to get the new feature in the tree
        const updatedFeatures = await loadFeatures();
        logFeatureChanges(featuresBefore, updatedFeatures);
        
        // Find the newly created feature in the updated tree
        const findFeature = (featuresList, targetId) => {
          for (const f of featuresList) {
            if (f.id === targetId) return f;
            if (f.subFeatures && f.subFeatures.length > 0) {
              const found = findFeature(f.subFeatures, targetId);
              if (found) return found;
            }
          }
          return null;
        };
        const createdFeature = findFeature(updatedFeatures, newFeature.id);
        if (createdFeature) {
          // Switch to view mode showing the newly created feature
          setViewingFeature(createdFeature);
          setEditingFeature(null);
          setPanelMode('view');
          // Update URL to include the new feature ID
          navigate(`/feature/${newFeature.id}`);
        } else {
          // Fallback: close panel if feature not found
          setPanelOpen(false);
          setEditingFeature(null);
          setViewingFeature(null);
          setParentFeature(null);
        }
      }
    } catch (err) {
      // Handle conflict errors specifically
      if (err.message === 'FEATURE_CONFLICT' || err.message?.includes('modified by another user')) {
        // Reload the feature to get the latest version
        if (editingFeature) {
          try {
            const refreshedFeature = await getFeatureById(editingFeature.id);
            setViewingFeature(refreshedFeature);
            setEditingFeature(refreshedFeature);
            // Set conflict error in FeaturePanel via prop (will be handled by FeaturePanel)
            setError('This feature was modified by another user. The latest version has been loaded. Please review the changes and try saving again.');
          } catch (reloadErr) {
            logger.error('Error reloading feature after conflict:', reloadErr);
            setError('This feature was modified by another user. Please reload the page.');
          }
        }
      } else {
        setError(handleError(err, 'Failed to save feature. Please try again.'));
        logger.error(err);
      }
    }
  };

  const handleProfileSave = async (profileUpdates) => {
    const updatedProfile = await updateUserProfile(profileUpdates);
    setUserProfile(updatedProfile);
  };

  const handleSaveAndClose = async (featureData, tagAssignments = [], selectedReleaseIds = []) => {
    try {
      const featuresBefore = JSON.parse(JSON.stringify(features)); // Deep copy
      
      if (editingFeature) {
        logger.debug('💾 SAVING AND CLOSING (UPDATE):', {
          name: featureData.name,
          id: editingFeature.id,
          parentName: getParentName(featureData.parent_id),
          parentId: featureData.parent_id || 'null'
        });
        
        await updateFeature(editingFeature.id, featureData);
        // Save tags if provided (tagAssignments format: [{tag_id, state, rating, comment}])
        if (tagAssignments !== undefined && tagAssignments.length > 0) {
          await setFeatureTags(editingFeature.id, tagAssignments);
        }
        // Save releases if provided
        if (selectedReleaseIds !== undefined) {
          await setFeatureReleases(editingFeature.id, selectedReleaseIds);
        }
      } else {
        logger.debug('💾 SAVING AND CLOSING (CREATE):', {
          name: featureData.name,
          id: 'NEW',
          parentName: getParentName(featureData.parent_id),
          parentId: featureData.parent_id || 'null'
        });
        
        const newFeature = await createFeature({ ...featureData, feature_group_id: selectedFeatureGroup });
        // Save tags for new feature (tagAssignments format)
        if (tagAssignments && tagAssignments.length > 0) {
          await setFeatureTags(newFeature.id, tagAssignments);
        }
        // Save releases for new feature
        if (selectedReleaseIds && selectedReleaseIds.length > 0) {
          await setFeatureReleases(newFeature.id, selectedReleaseIds);
        }
      }
      // Clear all state when closing
      setPanelOpen(false);
      setEditingFeature(null);
      setViewingFeature(null);
      setParentFeature(null);
      // Clear the feature ID from URL when closing (if opened via direct link)
      // Preserve the active tab
      if (location.pathname.startsWith('/feature/')) {
        navigate('/', { replace: true });
      }
      // Reload features to get updated data
      const updatedFeatures = await loadFeatures();
      logFeatureChanges(featuresBefore, updatedFeatures);
      // Expanded nodes will be preserved by FeatureTable component's internal state
    } catch (err) {
      setError(handleError(err, 'Failed to save feature. Please try again.'));
      logger.error(err);
    }
  };

  const handleSaveAndCopy = async (featureData, tagAssignments = [], selectedReleaseIds = []) => {
    try {
      const featuresBefore = JSON.parse(JSON.stringify(features)); // Deep copy
      let baseFeature = null;

      if (editingFeature) {
        logger.debug('💾 SAVING AND COPYING (UPDATE):', {
          name: featureData.name,
          id: editingFeature.id,
          parentName: getParentName(featureData.parent_id),
          parentId: featureData.parent_id || 'null'
        });

        // Save changes to the existing feature first
        await updateFeature(editingFeature.id, featureData);
        if (tagAssignments !== undefined && tagAssignments.length > 0) {
          await setFeatureTags(editingFeature.id, tagAssignments);
        }
        if (selectedReleaseIds !== undefined) {
          await setFeatureReleases(editingFeature.id, selectedReleaseIds);
        }

        // Reload features to get the latest sort_order for the base feature
        const updatedFeatures = await loadFeatures();

        const findFeatureById = (featuresList, targetId) => {
          for (const f of featuresList) {
            if (f.id === targetId) return f;
            if (f.subFeatures && f.subFeatures.length > 0) {
              const found = findFeatureById(f.subFeatures, targetId);
              if (found) return found;
            }
          }
          return null;
        };

        const updatedBase = findFeatureById(updatedFeatures, editingFeature.id);
        baseFeature = updatedBase || { ...editingFeature, ...featureData };
      } else {
        logger.debug('💾 SAVING AND COPYING (CREATE):', {
          name: featureData.name,
          id: 'NEW',
          parentName: getParentName(featureData.parent_id),
          parentId: featureData.parent_id || 'null'
        });

        // Create the original feature first
        const newFeature = await createFeature({ ...featureData, feature_group_id: selectedFeatureGroup });
        if (tagAssignments && tagAssignments.length > 0) {
          await setFeatureTags(newFeature.id, tagAssignments);
        }
        if (selectedReleaseIds && selectedReleaseIds.length > 0) {
          await setFeatureReleases(newFeature.id, selectedReleaseIds);
        }
        baseFeature = newFeature;
      }

      const baseSortOrder = baseFeature.sort_order || 0;

      // Prepare data for the copied feature
      const copyData = {
        ...featureData,
        name: `${featureData.name} (Copy)`,
        status: 'No Status', // Status should be empty for the copy
        sort_order: baseSortOrder + 1
      };

      // Create the copied feature right after the base feature
      const copiedFeature = await createFeature({
        ...copyData,
        feature_group_id: selectedFeatureGroup
      });

      // Copy tags, but NOT releases
      if (tagAssignments && tagAssignments.length > 0) {
        await setFeatureTags(copiedFeature.id, tagAssignments);
      }

      // Reload features to get the duplicated feature with all its data
      const finalFeatures = await loadFeatures();
      logFeatureChanges(featuresBefore, finalFeatures);

      // Find the duplicated feature in the updated tree
      const findFeatureById = (featuresList, targetId) => {
        for (const f of featuresList) {
          if (f.id === targetId) return f;
          if (f.subFeatures && f.subFeatures.length > 0) {
            const found = findFeatureById(f.subFeatures, targetId);
            if (found) return found;
          }
        }
        return null;
      };

      const duplicatedFeature = findFeatureById(finalFeatures, copiedFeature.id);
      
      // Open the duplicated feature in edit mode
      if (duplicatedFeature) {
        setEditingFeature(duplicatedFeature);
        setViewingFeature(duplicatedFeature);
        setParentFeature(null);
        setPanelMode('edit');
        setPanelOpen(true);
        // Update URL to the new feature
        navigate(`/feature/${duplicatedFeature.id}`);
      } else {
        // Fallback: close panel if we can't find the feature
        setPanelOpen(false);
        setEditingFeature(null);
        setViewingFeature(null);
        setParentFeature(null);
      }
    } catch (err) {
      setError(handleError(err, 'Failed to save and copy feature. Please try again.'));
      logger.error(err);
    }
  };

  const handleClosePanel = useCallback(() => {
    setPanelOpen(false);
    setEditingFeature(null);
    setViewingFeature(null);
    setParentFeature(null);
    // Clear the feature ID from URL when closing
    // Use replace: true to avoid adding to history stack
    // The activeTab state is preserved, so we'll stay on the current tab (e.g., Releases)
    if (location.pathname.startsWith('/feature/')) {
      navigate('/', { replace: true });
    }
  }, [location.pathname, navigate]);

  const handleMoveFeature = async (id, direction) => {
    // Optimistic UI update - update immediately
    const updateFeatureOrder = (features, targetId, dir) => {
      const findAndSwap = (items, targetId, dir) => {
        for (let i = 0; i < items.length; i++) {
          if (items[i].id === targetId) {
            const targetIndex = dir === 'up' ? i - 1 : i + 1;
            if (targetIndex >= 0 && targetIndex < items.length) {
              // Swap items
              [items[i], items[targetIndex]] = [items[targetIndex], items[i]];
              return true;
            }
            return false;
          }
          // Check sub-features
          if (items[i].subFeatures && items[i].subFeatures.length > 0) {
            if (findAndSwap(items[i].subFeatures, targetId, dir)) {
              return true;
            }
          }
        }
        return false;
      };
      
      const updatedFeatures = JSON.parse(JSON.stringify(features));
      findAndSwap(updatedFeatures, targetId, dir);
      return updatedFeatures;
    };

    // Store original state for rollback
    const originalFeatures = JSON.parse(JSON.stringify(features));
    
    // Optimistically update UI
    const updatedFeatures = updateFeatureOrder(features, id, direction);
    setFeatures(updatedFeatures);

    // Update backend asynchronously
    try {
      await moveFeature(id, direction);
      // Reload features to get updated sort_order values from database
      await loadFeatures();
    } catch (err) {
      // Rollback on error
      setFeatures(originalFeatures);
      setError(handleError(err, 'Failed to move feature. Please try again.'));
      logger.error(err);
    }
  };

  const getTabs = () => {
    const baseTabs = ['Home', 'Hierarchy', 'Releases', 'Tags'];
    if (userRole === 'Owner') {
      baseTabs.push('Competitors');
    }
    if (userRole !== 'Guest') {
      baseTabs.push('Analysis');
    }
    if (userRole === 'Owner') {
      return [...baseTabs, 'Admin'];
    }
    return baseTabs;
  };

  const handleSelectFeatureGroup = useCallback(async (featureGroupId) => {
    setSelectedFeatureGroup(featureGroupId);
    setActiveTab('Hierarchy');
    // Update last_visit when user selects a feature group
    if (user && featureGroupId) {
      try {
        await updateLastVisit(featureGroupId, user.id);
      } catch (err) {
        logger.error('Error updating last visit:', err);
      }
    }
    // Load features for the selected group - pass featureGroupId directly to avoid race condition
    await loadFeatures(true, featureGroupId);
  }, [user, loadFeatures]);

  const handleHomeTabClick = useCallback(() => {
    // Reset to initial state when Home tab is clicked
    setSelectedFeatureGroup(null);
    setActiveTab('Home');
    setUserRole(null);
    // Close any open panels
    setPanelOpen(false);
    setViewingFeature(null);
    setEditingFeature(null);
    setParentFeature(null);
    // Clear features
    setFeatures([]);
    // Clear filter
    setFilter('');
    // Clear expanded nodes
    setExpandedNodes(new Set());
  }, []);

  const tabs = getTabs();

  if (authLoading) {
    return (
      <div className="App">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Auth />;
  }

  return (
    <div className="App">
      <header className="App-header">
        <div 
          className="header-title"
          onClick={handleHomeTabClick}
          style={{ cursor: 'pointer' }}
        >
          <img 
            src="https://vmfkqkzaqslniowpftts.supabase.co/storage/v1/object/public/img/featuremap_logo.png" 
            alt="FeatureMap Logo" 
            className="logo"
          />
          <h1>FeatureMap</h1>
        </div>
        <div className="user-info">
          <UserMenu
            profile={userProfile}
            email={user.email}
            onSignOut={signOut}
            onNavigateProfile={() => setActiveTab('SystemProfile')}
            onNavigateSystemAdmin={() => setActiveTab('SystemAdmin')}
            isSuperuser={userProfile?.role === 'superuser'}
            onShowRecent={() => setShowRecentFeatures(true)}
          />
        </div>
      </header>
      
      <nav className="subnav">
        <div className="subnav-left">
          {featureGroups.length > 0 && selectedFeatureGroup && activeTab !== 'Home' && (
            <div className="feature-group-selector">
              <select
                value={selectedFeatureGroup || ''}
                onChange={async (e) => {
                  const newGroupId = e.target.value;
                  setSelectedFeatureGroup(newGroupId);
                  // Update last_visit when user selects a feature group
                  if (user && newGroupId) {
                    await updateLastVisit(newGroupId, user.id);
                  }
                  // Reload features for the new group - pass newGroupId directly to avoid race condition
                  await loadFeatures(true, newGroupId);
                }}
                className="feature-group-select"
              >
                {featureGroups.map(group => (
                  <option key={group.id} value={group.id}>
                    {group.display_name}
                  </option>
                ))}
              </select>
              {selectedFeatureGroup && (
                <span className={`role-badge role-${userRole?.toLowerCase() || 'guest'}`}>
                  {userRole || 'Guest'}
                </span>
              )}
            </div>
          )}
          {/* Show feature group selector when no feature map is selected */}
          {featureGroups.length > 0 && !selectedFeatureGroup && (
            <div className="feature-group-selector">
              <select
                value=""
                onChange={async (e) => {
                  const newGroupId = e.target.value;
                  if (newGroupId) {
                    await handleSelectFeatureGroup(newGroupId);
                  }
                }}
                className="feature-group-select"
              >
                <option value="">Please select a Feature Map</option>
                {featureGroups.map(group => (
                  <option key={group.id} value={group.id}>
                    {group.display_name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="subnav-tabs">
          <div className="mobile-menu-wrapper">
            <button
              className="mobile-menu-button"
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              disabled={!selectedFeatureGroup && activeTab !== 'Home'}
            >
              {activeTab} <i className={`fas fa-chevron-${showMobileMenu ? 'up' : 'down'}`}></i>
            </button>
            {showMobileMenu && (
              <div className="mobile-menu-dropdown">
                {tabs.map(tab => {
                  const isDisabled = !selectedFeatureGroup && tab !== 'Home';
                  return (
                    <button
                      key={tab}
                      className={`mobile-menu-item ${activeTab === tab ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
                      onClick={() => {
                        if (!isDisabled) {
                          setActiveTab(tab);
                          setShowMobileMenu(false);
                        }
                      }}
                      disabled={isDisabled}
                    >
                      {tab}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="desktop-tabs">
            {tabs.map(tab => {
              const isDisabled = !selectedFeatureGroup && tab !== 'Home';
              return (
                <button
                  key={tab}
                  className={`subnav-tab ${activeTab === tab ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
                  onClick={() => {
                    if (!isDisabled) {
                      if (tab === 'Home') {
                        handleHomeTabClick();
                      } else {
                        setActiveTab(tab);
                      }
                    }
                  }}
                  disabled={isDisabled}
                >
                  {tab}
                </button>
              );
            })}
          </div>
        </div>
      </nav>
      
      <main className="App-main">
        {loading && <div className="loading">Loading features...</div>}
        {error && <div className="error">{error}</div>}
        
        {!loading && !error && activeTab === 'Home' && (
          <Home
            onSelectFeatureGroup={handleSelectFeatureGroup}
            onOpenFeature={handleOpenFeatureFromHome}
          />
        )}

        {!loading && !error && activeTab === 'Hierarchy' && selectedFeatureGroup && (
          <FeatureTable
            features={features}
            filter={filter}
            onFilterChange={setFilter}
            onEdit={handleEditFeature}
            onDelete={handleDeleteFeature}
            onReloadFeatures={loadFeatures}
            onAddSubFeature={(parent) => handleAddFeature(parent)}
            onMove={handleMoveFeature}
            onAddFeature={handleAddFeature}
            onViewFeature={handleViewFeature}
            expandedNodes={expandedNodes}
            onExpandedNodesChange={setExpandedNodes}
            featureGroupId={selectedFeatureGroup}
            featureReleases={featureReleasesCache}
            mode={hierarchyMode}
            onModeChange={setHierarchyMode}
            userId={user?.id}
            userRole={userRole}
          />
        )}
        
        {!loading && !error && activeTab === 'Releases' && selectedFeatureGroup && (
          <Releases
            featureGroupId={selectedFeatureGroup}
            userRole={userRole}
            onViewFeature={handleViewFeature}
          />
        )}

        {!loading && !error && activeTab === 'Tags' && selectedFeatureGroup && (
          <Tags
            featureGroupId={selectedFeatureGroup}
            userRole={userRole}
          />
        )}

        {!loading && !error && activeTab === 'Competitors' && userRole === 'Owner' && selectedFeatureGroup && (
          <Competitors
            featureGroupId={selectedFeatureGroup}
            userRole={userRole}
          />
        )}

        {!loading && !error && activeTab === 'Admin' && userRole === 'Owner' && selectedFeatureGroup && (
          <FeatureGroupAdmin
            featureGroupId={selectedFeatureGroup}
            featureGroupName={featureGroups.find(g => g.id === selectedFeatureGroup)?.display_name || ''}
            featureGroupDescription={featureGroups.find(g => g.id === selectedFeatureGroup)?.description || ''}
            onUpdate={loadFeatureGroups}
            userRole={userRole}
          />
        )}

        {!loading && !error && ['Hierarchy', 'Releases', 'Tags', 'Competitors', 'Admin'].includes(activeTab) && !selectedFeatureGroup && (
          <div className="no-feature-group-selected">
            <p>Please select a Feature Map from the Home tab to continue.</p>
          </div>
        )}

        {!loading && !error && activeTab === 'SystemProfile' && (
          <SystemProfile
            profile={userProfile}
            loading={profileLoading}
            onSave={handleProfileSave}
          />
        )}

        {!loading && !error && activeTab === 'SystemAdmin' && (
          <SystemAdmin
            isSuperuser={userProfile?.role === 'superuser'}
          />
        )}
        
        {!loading && !error && !['Home', 'Hierarchy', 'Releases', 'Tags', 'Competitors', 'Admin', 'SystemProfile', 'SystemAdmin'].includes(activeTab) && (
          <div className="tab-content-placeholder">
            <p>{activeTab} content coming soon...</p>
          </div>
        )}
      </main>

      <FeaturePanel
        isOpen={panelOpen}
        mode={panelMode}
        feature={editingFeature || viewingFeature}
        parentFeature={parentFeature}
        allFeatures={features}
        featureGroupId={selectedFeatureGroup}
        onSave={handleSaveFeature}
        onSaveAndClose={handleSaveAndClose}
        onSaveAndCopy={handleSaveAndCopy}
        onClose={handleClosePanel}
        onEdit={handleStartEdit}
        onCancelToView={handleCancelToView}
        userRole={userRole}
        userId={user?.id || null}
        userProfile={userProfile}
        onImprovePromptStart={() => {
          // Show loading spinner immediately when button is clicked
          setImprovePromptLoading(true);
        }}
        onImprovePrompt={(feature) => {
          // FeaturePanel already handles saving before calling this
          // The feature passed here should already have all the latest saved data
          setImprovePromptFeature(feature);
          setShowImprovePrompt(true);
        }}
      />
      {/* Full-screen loading overlay when opening Improve Feature */}
      {improvePromptLoading && (
        <div className="improve-prompt-loading-overlay">
          <div className="improve-prompt-loading-spinner">
            <i className="fas fa-spinner fa-spin"></i>
          </div>
        </div>
      )}
      {showImprovePrompt && improvePromptFeature && (
        <ImprovePromptView
          feature={improvePromptFeature}
          featureGroupId={selectedFeatureGroup}
          onReady={() => {
            // Hide loading overlay when ImprovePromptView is ready
            setImprovePromptLoading(false);
          }}
          onClose={() => {
            setShowImprovePrompt(false);
            setImprovePromptFeature(null);
            setImprovePromptLoading(false);
          }}
          onSave={async (updatedFeature) => {
            // Reload features after save
            await loadFeatures();
            setShowImprovePrompt(false);
            setImprovePromptFeature(null);
            setImprovePromptLoading(false);
          }}
          onUpdateFeature={async (updatedFeature) => {
            // Update the feature without closing Improve Feature mode
            // Reload features to get latest data, then find the updated feature in the hierarchy
            await loadFeatures();
            // Use the updatedFeature directly since we just saved it
            // The loadFeatures will update the main features list, but we'll use the passed feature
            setImprovePromptFeature(updatedFeature);
          }}
        />
      )}
      {showRecentFeatures && (
        <RecentFeatures
          onClose={() => setShowRecentFeatures(false)}
          onNavigateToFeature={handleNavigateToFeature}
        />
      )}
      <Footer currentRelease={currentRelease} featureGroupId={footerFeatureGroupId} />
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/about" element={<About />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/buy-feature/:slug" element={<BuyFeaturePage />} />
      <Route path="/feature/:featureId" element={<AppContent />} />
      <Route path="*" element={<AppContent />} />
    </Routes>
  );
}

export default App;
